import { Inline } from '@bedrock-layout/primitives';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isUndefined from 'lodash/isUndefined';
import { useEffect, useState } from 'react';
import type {
  Control,
  UseFormGetValues,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { MdCable } from 'react-icons/md';
import { DropdownField, IconButton, TextButton, TextField, useLayer } from 'ui';

import { EntityDependencyUsage } from '../../../components/EntityDependencyUsage/EntityDependencyUsage';
import { PermissionWrapper } from '../../../components/PermissionComponent';
import { permissionObj } from '../../../components/PermissionComponent/constant';
import { useCheckPermissions } from '../../../components/PermissionComponent/hooks/useCheckPermissions';
import { handleSetCheckSumByEntityName } from '../../../utils/common';
import { ENTITY_ID } from '../../../utils/constant';
import { dataTypes } from '../constant';
import type {
  AddConfigValueType,
  GetRemoteConfigResult,
  RemoteConfigFieldsDataTypes,
  SavedConfigModel,
} from '../models';
import type { RemoteConfigFormValues } from './RemoteConfig';
import { FieldContainer, StackStyled } from './RemoteConfig.styled';
import { RemoteConfigDeleteButton } from './RemoteConfigDeleteButton';
import { RemoteConfigField } from './form/RemoteConfigComponents';
import { IconByDataType } from 'ui/src/ConditionPopovers/RulePopover/IconByDataType';

type HandleUpdateConfig = {
  id: string;
  value: AddConfigValueType;
  checksum: string;
  canReset: boolean;
  isSecret: boolean;
};

type TableDataProps = {
  control: Control<RemoteConfigFormValues>;
  getValues: UseFormGetValues<RemoteConfigFormValues>;
  trigger: UseFormTrigger<RemoteConfigFormValues>;
  savedConfigList: GetRemoteConfigResult;
  watch: UseFormWatch<Record<string, any>>;
  handleUpdateConfig: ({
    id,
    value,
    checksum,
    canReset,
    isSecret,
  }: HandleUpdateConfig) => void;
  handleDeleteConfig: (id: string, checksum: string) => void;
  isLoading: boolean;
  from: string;
};

export const TYPE_OPTIONS = [
  {
    label: 'Static',
    value: false,
    meta: {
      description: 'Fixed value manually entered',
    },
  },
  {
    label: 'Dynamic',
    value: true,
    meta: {
      description: 'Updated via workflow, rule',
    },
  },
];

export function TableData({
  savedConfigList,
  control,
  handleUpdateConfig,
  handleDeleteConfig,
  getValues,
  watch,
  trigger,
  isLoading,
  from,
}: TableDataProps) {
  const { isHide: disabledValueField } = useCheckPermissions({
    allowedPermission: [permissionObj.create, permissionObj.edit],
    entityList: [ENTITY_ID.remoteConfig],
  });
  const [savedConfigs, setSavedConfigs] = useState<SavedConfigModel[]>([]);

  const formData = watch();

  const firstGVarData = formData?.savedConfig?.data?.[0] ?? {};

  const { openWithProps: openDependencyUsage } = useLayer(
    <EntityDependencyUsage
      entityInfo={{
        type: ENTITY_ID.remoteConfig,
        status: '',
        name: firstGVarData?.name ?? '',
        id: firstGVarData?.id ?? '',
        subType: firstGVarData?.dataType ?? '',
      }}
      openInSheet={true}
    />
  );

  useEffect(() => {
    if (
      !_isNil(savedConfigList) &&
      savedConfigList.savedConfig.data.length > 0
    ) {
      const updatedConfigList = structuredClone(
        savedConfigList.savedConfig.data
      );
      setSavedConfigs(updatedConfigList);
    }
  }, [savedConfigList]);

  const handleUpdate = async (
    index: number,
    id: string,
    dataType: RemoteConfigFieldsDataTypes,
    checksum: string
  ) => {
    let fieldValue = formData.savedConfig.data[index].value;
    let canResetValue = formData.savedConfig.data[index].canReset;
    handleSetCheckSumByEntityName('remote-config', checksum);

    if (dataType === dataTypes.numeric && !_isEmpty(fieldValue)) {
      fieldValue = parseInt(fieldValue as string);
    }
    const requestValues = {
      id,
      value: fieldValue,
      checksum,
      isSecret: (from === 'enc' ? true : false),
      canReset: (from === 'enc' ? false : (canResetValue.value ?? canResetValue)),
    };

    if (
      dataType === dataTypes.boolean ||
      dataType === dataTypes.dateTime ||
      dataType === dataTypes.date
    ) {
      handleUpdateConfig({
        ...requestValues,
      });
    } else if (dataType === dataTypes.json) {
      handleUpdateConfig({
        ...requestValues,
        value: JSON.parse(requestValues.value),
      });
    } else if (
      (dataType === dataTypes.numeric || dataType === dataTypes.string) &&
      !_isUndefined(fieldValue) &&
      !_isEmpty(fieldValue.toString())
    ) {
      handleUpdateConfig({
        ...requestValues,
      });
    } else if (dataType === dataTypes.list) {
      handleUpdateConfig({
        ...requestValues,
        value: JSON.parse(requestValues.value),
      });
    } else {
      await trigger(`savedConfig.data.${index}.value`);
    }
  };

  const openGlobalVarUsage = (index: number) => {
    const currGVarObj = formData.savedConfig.data[index];

    openDependencyUsage({
      entityInfo: {
        type: ENTITY_ID.remoteConfig,
        status: '',
        name: currGVarObj.name,
        id: currGVarObj.id ?? '',
        subType: currGVarObj.dataType,
      },
      openInSheet: true,
    });
  };

  const getDropdownValue = (canReset: any) => {
    if (_isNil(canReset)) {
      return TYPE_OPTIONS[0];
    }

    return typeof canReset === 'object'
      ? canReset
      : TYPE_OPTIONS.find((option) => option.value === canReset) ||
          TYPE_OPTIONS[0];
  };

  return (
    <StackStyled gutter="1.6rem">
      {savedConfigs.map(({ id, name, value, dataType, checksum }, index) => {
        return (
          <Inline as="li" key={id} align="center">
            <FieldContainer>
              <TextField
                control={control}
                name={`savedConfig.data.${index}.name`}
                defaultValue={name}
                disabled
                startIcon={
                  <span>
                    <IconByDataType
                      dataType={dataType ?? 'string'}
                      color="var(--color-paleDodgerBlue)"
                    />
                  </span>
                }
              />
            </FieldContainer>

            <FieldContainer>
              <RemoteConfigField
                type={from === "enc"  && dataType !== 'json' ? 'password'  : dataType}
                control={control}
                name={`savedConfig.data.${index}.value`}
                value={value}
                disabled={disabledValueField}
                placeholder={value === null && from === "enc" ? '•••••••••••••••••••••••••' : `${value}`}
              />
            </FieldContainer>

            <Inline as={FieldContainer} align="center">
              {from === 'unenc' && (
                <PermissionWrapper
                  allowedPermission={[permissionObj.delete]}
                  entityList={[ENTITY_ID.remoteConfig]}
                >
                  <DropdownField
                    placeholder={'Select Type'}
                    control={control}
                    value={getDropdownValue(
                      formData.savedConfig.data[index].canReset
                    )}
                    name={`savedConfig.data.${index}.canReset`}
                    disabled={disabledValueField}
                    options={TYPE_OPTIONS}
                    showCustomMenuV3
                  />
                </PermissionWrapper>
              )}
              <PermissionWrapper
                allowedPermission={[permissionObj.delete]}
                entityList={[ENTITY_ID.remoteConfig]}
              >
                <RemoteConfigDeleteButton
                  title="Delete Attribute"
                  deleteConfig={(id) => handleDeleteConfig(id, checksum)}
                  deletingIndex={id}
                />
              </PermissionWrapper>

              <IconButton onClick={() => openGlobalVarUsage(index)}>
                <MdCable color="var(--color-darkGray)" />
              </IconButton>

              <PermissionWrapper
                allowedPermission={[permissionObj.create, permissionObj.edit]}
                entityList={[ENTITY_ID.remoteConfig]}
              >
                <TextButton
                  disabled={isLoading}
                  onClick={async () =>
                    await handleUpdate(index, id, dataType, checksum)
                  }
                >
                  Update
                </TextButton>
              </PermissionWrapper>
            </Inline>
          </Inline>
        );
      })}
    </StackStyled>
  );
}
